<!-- 
  ===============================================================================
  Using this component to displaying new configuration form to user with below text-fields.
  1.configLabel
  2.configId
  3.scope
  4.clientId
  5.clientSecret
  6.redirectURI
  7.authorizationURI
  8.tokenURI
  9.subdomain
  10.Grant flow fields
  ===============================================================================
-->
<template>
  <v-dialog
    :value="showDialogNewConfiguration"
    max-width="600px"
    persistent
  >
    <v-card class="mx-auto mt-5 pa-4">
      <!-- New configuration form info message tooltip -->
      <CardTitleWithTooltip>
        <template v-slot:title>
          {{ $t('customer.configuration.new') }}
        </template>
        <template v-slot:tooltip>
          {{ $t('customer.configuration.newTooltip') }}
        </template>
      </CardTitleWithTooltip>
      <!-- New configuration form fields -->
      <v-card-text class="py-0 px-3">
        <v-form ref="newAccountForm" v-model="newAccountFormValidity" @submit.prevent="saveNewAccount">
          <v-text-field 
            :label="$t('customer.configuration.configurationId')" 
            type="text"
            v-model="newAccount.configId"
            v-show="false"
          ></v-text-field>
          <v-container>
            <v-row>
              <!-- Configuration Label text-field -->
              <v-col cols="12" class="pa-0 ma-0">
                <v-text-field 
                  :label="$t('customer.configuration.configurationLabel')+'*'" 
                  type="text"
                  v-model="newAccount.configLabel"
                  :rules="configurationRules.configLabel"
                  tabindex="1"
                  required
                ></v-text-field>
              </v-col>
              <!-- Authorization Uri text-field -->
              <v-col cols="12" class="pa-0 ma-0">
                <v-text-field 
                  :label="$t('customer.configuration.authorizationUri')+'*'" 
                  type="text"
                  v-model="newAccount.authorizationURI"
                  :rules="configurationRules.authorizationURI"
                  tabindex="2"
                  required
                ></v-text-field>
              </v-col>
              <!-- Token Uri text-field -->
              <v-col cols="12" class="pa-0 ma-0">
                <v-text-field 
                  :label="$t('customer.configuration.tokenUri')+'*'" 
                  type="text"
                  v-model="newAccount.tokenURI"
                  :rules="configurationRules.tokenURI"
                  tabindex="3"
                  required
                ></v-text-field>
              </v-col>
              <!-- Client Id text-field -->
              <v-col cols="12" class="pa-0 ma-0">
                <v-text-field 
                  :label="$t('customer.configuration.clientId')+'*'" 
                  :type="showClientId ? 'text' : 'password'"
                  v-model="newAccount.clientId"
                  :rules="configurationRules.clientId"
                  :append-icon="showClientId ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showClientId = !showClientId"
                  tabindex="4"
                  required
                ></v-text-field>
              </v-col>
              <!-- Client Secret text-field -->
              <v-col cols="12" class="pa-0 ma-0">
                <v-text-field 
                  :label="$t('customer.configuration.clientSecret')+'*'" 
                  :type="showClientSecret ? 'text' : 'password'"
                  v-model="newAccount.clientSecret"
                  :rules="configurationRules.clientSecret"
                  :append-icon="showClientSecret ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showClientSecret = !showClientSecret"
                  tabindex="5"
                  required
                ></v-text-field>
              </v-col>
              <!-- Redirect Uri text-field -->
              <v-col cols="12" class="pa-0 ma-0">
                <v-text-field 
                  :label="$t('customer.configuration.redirectUri')+'*'" 
                  type="text"
                  v-model="newAccount.redirectURI"
                  :rules="configurationRules.redirectURI"
                  tabindex="6"
                  required
                ></v-text-field>
              </v-col>
              <!-- Scope text-field -->
              <v-col cols="12" class="pa-0 ma-0">
                <v-text-field 
                  :label="$t('customer.configuration.scope')+'*'" 
                  type="text"
                  v-model="newAccount.scope"
                  tabindex="7"
                  required
                ></v-text-field>
              </v-col>
              <!-- Subdomain text-field -->
              <v-col cols="12" class="pa-0 ma-0">
                <v-text-field 
                  :label="$t('customer.configuration.subdomain')+'*'" 
                  type="text"
                  v-model="newAccount.subdomain"
                  :rules="configurationRules.subdomain"
                  tabindex="8"
                  required
                ></v-text-field>
              </v-col>
              <!-- Salesforce Auth Grant flow child component -->
              <SalesforceGrantForm
                :configuration="{}" 
                v-on:childToParent="salesforceGrantInfo"
              />
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions class="px-3 py-1">
        <!-- Close and Save buttons -->
        <v-btn
          @click="$emit('close-dialog')"
          color="red darken-1"
          tabindex="9"
          dark
        >{{ $t('close') }}</v-btn>
        <v-spacer></v-spacer>
        <v-btn
          @click="dialogNewAccountConfirm = true"
          :disabled="!newAccountFormValidity"
          color="green darken-1"
          tabindex="10"
          dark
        >
          {{ $t('save') }}
        </v-btn>
        <!-- Confirmation dialog -->
        <ConfirmDialog
          v-if="dialogNewAccountConfirm"
          :confirmDialogShow="dialogNewAccountConfirm"
          @confirm-dialog-close="dialogNewAccountConfirm = false"
          @confirm-dialog-save="saveForm"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  /** 
   @desc Importing required modules 
  */
  import { customerMixin } from '../mixins/customerMixins'
  import { mapGetters } from 'vuex'

  export default {
    /**
      * @desc Intalizing passed data from a SalesforceAccount.vue component
      * $showDialogNewConfiguration: User selected button status new config form/ edit configuration
    */
    props: ['showDialogNewConfiguration'],
    mixins: [customerMixin],
    components: {
       /**
        * @desc Imported component
        * CardTitleWithTooltip: To display tootip about new configuration form.
        * ConfirmDialog: To display confirmation dialog after save button action happened.
        * SalesforceGrantForm: To display grant flow fields.
      */
      CardTitleWithTooltip: require('../components/CardTitleWithTooltip').default,
      ConfirmDialog: () => import('@/components/ConfirmDialog'),
      SalesforceGrantForm: () => import('@/components/SalesforceGrantForm'),
    },
    computed: {
      ...mapGetters({
        getCrmInstance: 'users/getCrmInstance', // Fetching CRM Instance value
      }),
    },
    created() {
      // CRM Instance
      this.crmInstance = this.getCrmInstance;
      // Setting pre-filled values to redirectURI and authorizationURI
      this.newAccount.redirectURI  = this.getPreFilledValue('redirectURI', this.crmInstance);
      // TODO: Feature version
      // this.newAccount.authorizationURI = this.getPreFilledValue('authorizationURI', this.crmInstance);
    },
    data () {
      return {
        crmInstance: '',
        dialogNewAccountConfirm: false,
        newAccountFormValidity: false,
        newAccount: {
          configLabel: '',
          configId: '',
          scope: '',
          clientId: '',
          clientSecret: '',
          redirectURI: '',
          authorizationURI: '',
          tokenURI: '',
          sfServerAuth : {}, // Salesforce grant info
          subdomain: '',
        },
        showClientSecret: false,
        showClientId: false
      }
    },
    methods: {
      /**
        * @desc Clear the text-fields data into newConfiguration form
      */
      resetNewAccountForm () {
        this.$refs.newAccountForm.reset()
      },
      /**
        * @desc Reset newConfiguration form text-fields validation rules
      */
      resetNewAccountFormValidation () {
        this.$refs.newAccountForm.resetValidation()
      },
      /**
        * @desc Binding child component salesforce grant information into parent component
        * @param object grantData: Contains grant_type, username ,password(based on selected grant flow) information 
      */
      salesforceGrantInfo(grantData) {
        this.newAccount.sfServerAuth = grantData;
      },
      /**
        * @desc Emitting save-account event to save newly added configuration
      */
      saveForm () {
        this.$emit('save-account', this.newAccount)
      }
    }
  }
</script>

<style lang="scss" scoped>
</style>